import { useEffect, useMemo, useState } from 'react'
import { ActivityIndicator, SafeAreaView, StatusBar, StyleSheet, Text, useColorScheme, View } from 'react-native'
import { LinearGradient } from 'expo-linear-gradient'
import { Theme, relayablyDarkTheme, relayablyLightTheme } from '@monorepo/relayably/theme'

import { completeLinkedInSession } from './auth/authSlice'
import { useAppDispatch } from './store'

const hexToRgba = (hex: string, alpha: number) => {
  const normalized = hex.replace('#', '')
  const value = normalized.length === 3 ? normalized.split('').map(char => `${char}${char}`).join('') : normalized
  const r = parseInt(value.slice(0, 2), 16)
  const g = parseInt(value.slice(2, 4), 16)
  const b = parseInt(value.slice(4, 6), 16)

  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

export const LinkedInCallbackExperience = () => {
  const dispatch = useAppDispatch()
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [message, setMessage] = useState('Completing your LinkedIn sign-in and restoring your Relayably session.')
  const colorScheme = useColorScheme()
  const theme = colorScheme === 'dark' ? relayablyDarkTheme : relayablyLightTheme
  const styles = useMemo(() => createStyles(theme), [theme])

  useEffect(() => {
    const url = typeof window !== 'undefined' ? new URL(window.location.href) : null
    const complete = async () => {
      try {
        if (!url?.searchParams.get('token')) {
          throw new Error('The LinkedIn callback did not include a token. Retry the sign-in flow from Relayably.')
        }

        await dispatch(completeLinkedInSession()).unwrap()

        setStatus('success')
        setMessage('LinkedIn sign-in complete. Relayably can now continue into onboarding or your dashboard.')
      } catch (error) {
        setStatus('error')
        setMessage(error instanceof Error ? error.message : 'LinkedIn sign-in failed.')
      }
    }

    void complete()
  }, [dispatch])

  return (
    <LinearGradient
      colors={[
        theme.dark ? theme.colors.background : hexToRgba(theme.colors.primary, 0.94),
        hexToRgba(theme.colors.secondary, theme.dark ? 0.48 : 0.7),
        theme.dark ? theme.colors.surface : '#f7f7fb',
      ]}
      locations={[0, 0.42, 1]}
      style={styles.screen}
    >
      <StatusBar barStyle={theme.dark ? 'light-content' : 'dark-content'} />
      <SafeAreaView style={styles.screen}>
        <View style={styles.card}>
          <Text style={styles.kicker}>LinkedIn Callback</Text>
          <Text style={styles.title}>
            {status === 'loading' ? 'Finishing web sign-in' : status === 'success' ? 'Signed in' : 'Sign-in issue'}
          </Text>
          <Text style={styles.copy}>{message}</Text>
          {status === 'loading' ? <ActivityIndicator color={theme.colors.primary} size='large' style={styles.spinner} /> : null}
        </View>
      </SafeAreaView>
    </LinearGradient>
  )
}

const createStyles = (theme: Theme) =>
  StyleSheet.create({
    screen: {
      flex: 1,
    },
    card: {
      alignItems: 'center',
      backgroundColor: theme.dark ? hexToRgba(theme.colors.card, 0.92) : hexToRgba(theme.colors.card, 0.9),
      borderColor: hexToRgba(theme.colors.primary, theme.dark ? 0.28 : 0.18),
      borderRadius: 28,
      borderWidth: 1,
      margin: 24,
      padding: 28,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 18 },
      shadowOpacity: 0.22,
      shadowRadius: 24,
    },
    kicker: {
      color: theme.colors.primary,
      fontSize: 12,
      fontWeight: '700',
      letterSpacing: 2.2,
      textTransform: 'uppercase',
    },
    title: {
      color: theme.colors.text,
      fontFamily: 'Georgia',
      fontSize: 30,
      lineHeight: 36,
      marginTop: 14,
      textAlign: 'center',
    },
    copy: {
      color: theme.colors.inactive,
      fontSize: 16,
      lineHeight: 24,
      marginTop: 14,
      maxWidth: 420,
      textAlign: 'center',
    },
    spinner: {
      marginTop: 22,
    },
  })
